import { useIntl } from 'react-intl';
import useSearch from 'src/registry/hooks/useSearch';
import usePagination from 'src/registry/hooks/usePagination';
import { createColumnHelper } from '@tanstack/react-table';
import {
    CellBadge,
    CellDatetime,
    DataTable,
    DataTableColumnHeader,
    DataTableControls,
    DataTablePagination,
} from 'src/registry/components/table';
import { CellLinkWithIcon } from 'src/registry/components/table/cells/CellLinkWithIcon';
import { commonMessages, generalMessages } from 'src/utils/messages';
import { paymentsMessages } from 'src/features/payments/utils/messages';
import { discountPoliciesMessages } from 'src/features/discountPolicies/utils/messages';
import { useListVoucherTypes } from 'src/features/voucherTypes/api/useListVoucherTypes';
import { voucherTypesMessages } from 'src/features/voucherTypes/utils/messages';
import { VoucherType } from 'src/features/voucherTypes/utils/types';
import { CellBoolean } from 'src/registry/components/table/cells/CellBoolean';
import { VOUCHER_USAGE_BADGE_OPTIONS } from 'src/features/voucherTypes/utils/constants';
import CreateVoucherTypeFormModal from 'src/features/voucherTypes/components/forms/CreateVoucherTypeFormModal';
import CellVoucherTypeActions from 'src/features/voucherTypes/components/tables/CellVoucherTypeActions';
import useFilters from 'src/registry/hooks/useFilters';
import { INPUT_TYPE } from 'src/registry/components/table/utils/constants';
import React from 'react';
import useSorting from 'src/registry/hooks/useSorting';
import useHasPermissions from 'src/utils/useHasPermissions';
import { PERMISSION } from 'src/features/auth/types';

const VoucherTypesTable = () => {
    const { formatMessage, formatNumber } = useIntl();
    const [dateNow] = React.useState(() => Date.now());

    const permissionManageVouchers = useHasPermissions([PERMISSION.MANAGE_VOUCHERS]);

    const { querySearchOptions, ...search } = useSearch();
    const { queryPaginationOptions, ...pagination } = usePagination();
    const { queryFiltersOptions, ...filters } = useFilters();
    const { querySortingOptions, ...sorting } = useSorting({
        initialSortingState: [{ id: 'valid_from', desc: false }],
    });

    const {
        data: voucherTypes,
        isLoading,
        isError,
    } = useListVoucherTypes({
        ...querySearchOptions,
        ...queryPaginationOptions,
        ...queryFiltersOptions,
        ...querySortingOptions,
    });

    const columnHelper = createColumnHelper<VoucherType>();

    const columns = [
        columnHelper.accessor('name', {
            enableSorting: true,
            header: DataTableColumnHeader,
            cell: (ctx) => <CellLinkWithIcon {...ctx} to={`/voucher-types/${ctx.row.original.id}`} />,
            meta: {
                title: formatMessage(commonMessages.name),
            },
        }),
        columnHelper.accessor('code', {
            enableSorting: true,
            header: DataTableColumnHeader,
            enableHiding: true,
            meta: {
                title: formatMessage(voucherTypesMessages.code),
            },
        }),
        columnHelper.accessor('valid_from', {
            header: DataTableColumnHeader,
            enableHiding: true,
            enableColumnFilter: true,
            enableSorting: true,
            cell: CellDatetime,
            meta: {
                title: formatMessage(generalMessages.validFrom),
                inputType: INPUT_TYPE.DATE,
                dateFilter: {
                    afterDate: new Date(dateNow - 30 * 24 * 60 * 60 * 1000),
                    beforeDate: new Date(),
                },
                inlineDateFilter: true,
            },
        }),
        columnHelper.accessor('valid_to', {
            header: DataTableColumnHeader,
            enableHiding: true,
            enableColumnFilter: true,
            enableSorting: true,
            cell: CellDatetime,
            meta: {
                title: formatMessage(generalMessages.validTo),
                inputType: INPUT_TYPE.DATE,
                dateFilter: {
                    afterDate: new Date(dateNow - 30 * 24 * 60 * 60 * 1000),
                    beforeDate: new Date(),
                },
                inlineDateFilter: true,
            },
        }),
        columnHelper.accessor('is_active', {
            enableSorting: true,
            header: DataTableColumnHeader,
            enableHiding: true,
            cell: CellBoolean,
            meta: {
                title: formatMessage(voucherTypesMessages.isActive),
            },
        }),

        columnHelper.accessor('usage', {
            enableSorting: true,
            header: DataTableColumnHeader,
            enableHiding: true,
            enableColumnFilter: true,
            cell: CellBadge,
            meta: {
                title: formatMessage(voucherTypesMessages.usage),
                badgeOptions: VOUCHER_USAGE_BADGE_OPTIONS,
            },
        }),
        columnHelper.accessor('percent', {
            enableSorting: true,
            header: DataTableColumnHeader,
            enableHiding: true,
            cell: ({ row }) => {
                const percent = row.original.percent;
                const displayTaxPercent = (Number(percent) * 100).toFixed(2);
                return percent != null ? `${displayTaxPercent}%` : '-';
            },
            meta: {
                title: formatMessage(discountPoliciesMessages.percent),
            },
        }),
        columnHelper.accessor('amount', {
            enableSorting: true,
            header: DataTableColumnHeader,
            enableHiding: true,
            cell: ({ row }) => {
                return row.original.amount && row.original.currency
                    ? formatNumber(row.original.amount / 100, {
                          style: 'currency',
                          currency: row.original.currency,
                      })
                    : '-';
            },
            meta: {
                title: formatMessage(paymentsMessages.amount),
            },
        }),
        columnHelper.display({
            id: 'actions',
            header: DataTableColumnHeader,
            cell: CellVoucherTypeActions,
            maxSize: 45,
        }),
    ];

    return (
        <DataTable<VoucherType, unknown>
            columns={columns}
            data={voucherTypes}
            semantics={{
                title: formatMessage(voucherTypesMessages.voucherTypes),
                entryName: formatMessage(voucherTypesMessages.voucherTypeTableBadge).toLowerCase(),
                entryNames: formatMessage(voucherTypesMessages.voucherTypesTableBadge).toLowerCase(),
                placeholder: formatMessage(voucherTypesMessages.voucher).toLowerCase(),
            }}
            search={search}
            pagination={pagination}
            filters={filters}
            sorting={sorting}
            isError={isError}
            isLoading={isLoading}
            getRowId={(row) => row.id.toString()}
            components={{
                controls: DataTableControls,
                pagination: DataTablePagination,
                ...(permissionManageVouchers && { newEntry: CreateVoucherTypeFormModal }),
            }}
        />
    );
};

export default VoucherTypesTable;
